import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { FormControlNames, FormItemTypes } from "../../../utils/formBuilderUtils";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import DatePickerCustom from "./DatePickerCustom";
import SimpleDatePicker from "./SimpleDatePicker";

const selectedColor = "var(--primary)";
const nonSelectedColor = "rgba(0,0,0,0.1)";
const dateFormat = "yyyy, MMM dd";

const renderItem = (item) => {
  switch (item.controlName) {
    case FormControlNames.INPUTTEXTFIELD:
      return (
        <TextField
          type={item.dataType}
          fullWidth={true}
          placeholder={item.placeholder}
          disabled
          variant="outlined"
        />
      );
    case FormControlNames.PHONEFIELD:
      return (
        <TextField
          type={item.dataType}
          fullWidth={true}
          placeholder={item.placeholder}
          disabled
          variant="outlined"
        />
      );
    case FormControlNames.INPUTMULTILINE:
      return (
        <TextField
          type={item.dataType}
          fullWidth={true}
          multiline={true}
          minRows={item.rows}
          placeholder={item.placeholder}
          disabled
          variant="outlined"
        />
      );
    case FormControlNames.CHECKBOX:
      return (
        <div className="m-t-20 p-l-0">
          <FormControlLabel
            control={<Checkbox disabled />}
            style={{ marginLeft: "0px" }}
            label={item.placeholder}
          />
        </div>
      );
    case FormControlNames.RADIOGROUP:
      return (
        <FormControl>
          <RadioGroup name={item.controlName + item.id} row>
            {item.items?.map((i) => (
              <FormControlLabel
                value={i.value}
                key={i.value}
                control={<Radio />}
                label={i.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    case FormControlNames.SELECTDROPDOWN:
      return (
        <FormControl style={{ minWidth: "100%" }}>
          <Select
            variant="outlined"
            disabled
            value={item.items && item.items[0].value}
          >
            {item.items?.map((i, ind) => (
              <MenuItem key={i.value} value={i.value}>
                {i.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case FormControlNames.DATEFIELD:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <SimpleDatePicker slotProps={{ textField: { fullWidth: true } }} disabled />
        </LocalizationProvider>
      );

    case FormControlNames.TIMEFIELD:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TimePicker disabled slotProps={{ textField: { fullWidth: true } }} />
        </LocalizationProvider>
      );
    case FormControlNames.FILEUPLOAD:
      return (
        <>
          <input style={{ display: "none" }} id={item.controlName + item.id} type="file" />
          <label className="control-input-trigger-buttons" htmlFor={item.controlName + item.id}>
            <i className="fas fa-cloud-upload-alt"></i>
          </label>
        </>
      );
    case FormControlNames.IMAGEUPLOAD:
      return (
        <>
          <input style={{ display: "none" }} id={item.controlName + item.id} type="file" />
          <label className="control-input-trigger-buttons" htmlFor={item.controlName + item.id}>
            <i className="far fa-image"></i>
          </label>
        </>
      );
    case FormControlNames.CAPTURECLICK:
      return (
        <>
          <input style={{ display: "none" }} id={item.controlName + item.id} type="file" />
          <label className="control-input-trigger-buttons" htmlFor={item.controlName + item.id}>
            <i className="fas fa-camera"></i>
          </label>
        </>
      );
    case FormControlNames.SCANCODE:
      return (
        <>
          <input style={{ display: "none" }} id={item.controlName + item.id} type="file" />
          <label className="control-input-trigger-buttons" htmlFor={item.controlName + item.id}>
            <i className="fas fa-qrcode"></i>
          </label>
        </>
      );
    case FormControlNames.SIGNATURE:
      return (
        <label
          className="control-input-trigger-buttons"
          style={{ width: "270px" }}
          htmlFor={item.controlName + item.id}
        >
          <span className="sign-label">Sign Here</span>
        </label>
      );
    case FormControlNames.TOGGLE:
      return <Switch checked={true} />;
    case FormControlNames.CHECKLIST:
      return (
        <FormGroup>
          {item.items?.map((i, ind) => (
            <FormControlLabel
              key={i.value}
              control={<Checkbox />}
              label={i.label}
              style={{ marginLeft: "0px" }}
            />
          ))}
        </FormGroup>
      );
    default:
      return null;
  }
};

function ControlViewComponent(props) {
  const { item, deleteControl, containerId, selectControl, selectedControl, index, moveControl } = props;

  let colBackgroundcolor = nonSelectedColor;
  let color = "";
  let wrapperStyle = {
    border: "1px solid " + nonSelectedColor,
    borderRadius: "9px",
    marginBottom: "20px",
    backgroundColor: "white",
    cursor: "pointer",
    boxShadow: "0 9px 90px #efefef",
  };

  // Check if it's the same type and id to change color.
  if (selectedControl && item.id === selectedControl.id && item.type === selectedControl.type) {
    wrapperStyle.border = "2px solid " + selectedColor;
    colBackgroundcolor = selectedColor;
    color = "white";
  }

  const handleDeleteControl = (event) => {
    deleteControl(item.id, containerId);
    if (event.stopPropagation) event.stopPropagation();
  };

  // Drag & Sort Code for functionality
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: FormItemTypes.CONTROL,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveControl(item, dragIndex, hoverIndex, containerId);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: FormItemTypes.CONTROL,
    item: () => {
      return { ...item, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className="row w-100 align-items-stretch justify-content-end"
      onClick={() => selectControl(item)}
      style={{ ...wrapperStyle, opacity }}
    >
      <div className="col-12 p-20">
        <div className="d-flex align-items-center justify-content-between">
          <h5>{item.labelName + (item.required ? " *" : "")}</h5>
          <div className="control-actions">
            <span style={{ cursor: "grab" }}>
              <i className="fa fa-ellipsis-v"></i>
              <i className="fa fa-ellipsis-v"></i>
            </span>
            <span onClick={handleDeleteControl}>
              <i className="fa fa-trash"></i>
            </span>
          </div>
        </div>
        {item.description && (
          <div className="mt-2">
            <p>{item.description}</p>
          </div>
        )}
        {renderItem(item)}
      </div>
    </div>
  );
}

export default ControlViewComponent;
